import { t } from '../i18n';

/**
 * Full-screen loading state shown until we have route data. Deliberately
 * covers the whole viewport — we never want to flash the wrong map location
 * before fitBounds runs.
 *
 * The `stage` prop lets us show finer-grained copy ("Verifying share
 * code…" → "Loading route…") if boot takes a beat.
 */
export type LoadingStage = 'idle' | 'session' | 'verify' | 'fetch';

const STAGE_COPY: Record<LoadingStage, string> = {
  idle: 'loading.title',
  session: 'loading.title',
  verify: 'loading.verifying',
  fetch: 'loading.fetching',
};

export function LoadingScreen({ stage }: { stage: LoadingStage }) {
  const copyKey = STAGE_COPY[stage] ?? 'loading.title';
  return (
    <main class="min-h-dvh flex flex-col items-center justify-center bg-background text-center px-6">
      <div class="flex flex-col items-center gap-6">
        {/* Brand mark: navy backdrop with the legacy white logo inside.
            The PNG is white-on-transparent, designed to sit on a coloured
            surface — letting the primary navy show through gives us the
            actual KommGutHeim / ArriveSafe icon without re-rendering it. */}
        <div class="relative flex h-24 w-24 items-center justify-center rounded-2xl bg-primary shadow-lg">
          <img
            src="/logo-white.png"
            alt={t('brand')}
            class="h-16 w-auto select-none"
            draggable={false}
          />
        </div>
        <div class="space-y-2">
          <h1 class="text-lg font-semibold text-primary">{t('brand')}</h1>
          <p class="text-sm text-text-secondary" aria-live="polite">{t(copyKey as never)}</p>
        </div>
        <div class="w-32 h-1 overflow-hidden rounded-full bg-surface-muted" aria-hidden="true">
          <div class="h-full w-1/3 bg-secondary motion-safe:animate-[loadingBar_1.6s_ease-in-out_infinite]" />
        </div>
      </div>
    </main>
  );
}
